import useSWR from "swr"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import type { TFormattedMoney } from "@sembark-travel/money"
import type { IContact } from "../Contacts"
import type { THotelAddon } from "../HotelAddOns"
import type { TOtaMealPlan } from "../HotelOtaMealPlans"
import type { TOtaRoomType } from "../HotelOtaRoomTypes"
import type { IHotel } from "../Hotels"
import type { ITripSource } from "../TripSources"
import type { ITrip } from "../Trips"
import type { TBatch } from "../shared/Batch"

// The stage of a booking, also what the channel manager believes the booking is in
export type TOtaBookingStageState = "booked" | "cancelled"

// Cancelling writes a new stage, not a new version
export type TOtaBookingStage = {
	state: TOtaBookingStageState
	created_at: string
}

export type TOtaBookingChild = {
	age: number
	count: number
}

// A version of the booking, `latest_booking` is the one that stands
export type TOtaBookingVersion = {
	id: number
	version: number
	is_latest: boolean
	guest_name: string
	guest_email: string | null
	guest_phone_number: string | null
	checkin_date: string
	checkin_date_local: string
	checkout_date: string
	checkout_date_local: string
	no_of_adults: number
	children: Array<TOtaBookingChild>
	created_at: string
}

export type TOtaBookingRoom = {
	room_key: string
	ota_room_type: Pick<TOtaRoomType, "name"> & { image_url: string }
	ota_meal_plan: Pick<TOtaMealPlan, "name">
	no_of_nights: number
	amount_money: TFormattedMoney
	tax_money: TFormattedMoney
	base_pax: number
	adults_with_extra_bed: number
	children_with_extra_bed_ages: Array<number>
	children_without_extra_bed_ages: Array<number>
	children_complimentary_ages: Array<number>
	nights: Array<{
		date: string
		date_local: string
		amount_money: TFormattedMoney
	}>
}

export type TOtaBookingExtraService = {
	id: number
	ota_addon: Pick<
		THotelAddon,
		"name" | "image_url" | "description" | "is_service_date_flexible"
	>
	date: string
	date_local: string
	quantity: number
	amount_money: TFormattedMoney
	tax_money: TFormattedMoney
}

// The version with its rooms and extras, loaded only on the detail and the versions
export type TOtaBookingVersionWithRooms = TOtaBookingVersion & {
	rooms: Array<TOtaBookingRoom>
	no_of_rooms: number
	extra_services: Array<TOtaBookingExtraService>
	no_of_extra_services: number
	total_amount_money: TFormattedMoney
}

export type TOtaBookingPayment = {
	id: number
	amount_money: TFormattedMoney
	paid_at: string
	method: string | null
	reference: string | null
}

export type TOtaBooking = {
	id: number
	display_id: string | null
	hotel: Pick<IHotel, "name" | "location"> & {
		image_url: string
	}
	trip_source: Pick<ITripSource, "name"> | null
	is_b2b: boolean
	trip: Pick<ITrip, "id"> | null
	total_amount_money: TFormattedMoney
	paid_amount_money: TFormattedMoney
	due_amount_money: TFormattedMoney
	booking_confirmation_number: string | null
	remote_booking_in_progress: boolean
	remote_booking_is_synced: boolean
	remote_booking_synced_at: string | null
	remote_booking_sync_attempted_at: string | null
	// null means the booking was never pushed/synced to Channel Manager
	remote_synced_booking: TOtaBookingVersion | null
	latest_booking: TOtaBookingVersion
	latest_stage: TOtaBookingStage
}

export type TOtaBookingDetail = Omit<TOtaBooking, "latest_booking"> & {
	latest_booking: TOtaBookingVersionWithRooms
	trip_source_contact: Pick<IContact, "name" | "email" | "phone_number"> | null
	payments: Array<TOtaBookingPayment>
	stages: Array<TOtaBookingStage>
}

export function hotelOtaBookingsXHR(xhr: XHRInstance) {
	return {
		async getOtaBookings(params?: Record<string, unknown>): Promise<
			IListResponse<TOtaBooking> & {
				meta: {
					// The counts the list carries with include=warnings
					warnings?: { not_synced: number }
				}
			}
		> {
			return xhr
				.get("/ota-hotel-bookings", { params })
				.then((resp) => resp.data)
		},
		async getOtaBooking(id: number): Promise<TOtaBookingDetail> {
			return xhr.get(`/ota-hotel-bookings/${id}`).then((resp) => resp.data.data)
		},
		// Newest first, each version with its rooms and extras
		async getOtaBookingVersions(
			id: number,
			params?: Record<string, unknown>
		): Promise<IListResponse<TOtaBookingVersionWithRooms>> {
			return xhr
				.get(`/ota-hotel-bookings/${id}/versions`, { params })
				.then((resp) => resp.data)
		},
		// Queues a push to the channel manager, a 409 when one is already under way
		async syncOtaBookingToRemote(
			id: number
		): Promise<{ message: string; data: TBatch }> {
			return xhr
				.post(`/ota-hotel-bookings/${id}/sync-to-remote`)
				.then((resp) => resp.data)
		},
	}
}

export function useHotelOtaBooking(id: number) {
	const xhr = useXHR()
	const { data, error, isLoading, mutate } = useSWR(
		`/ota-hotel-bookings/${id}`,
		() => hotelOtaBookingsXHR(xhr).getOtaBooking(id)
	)
	return { booking: data, isLoading, error, refresh: mutate }
}

export function useHotelOtaBookingVersions(bookingId: number) {
	const xhr = useXHR()
	const { data, error, isLoading } = useSWR(
		`/ota-hotel-bookings/${bookingId}/versions`,
		() =>
			hotelOtaBookingsXHR(xhr).getOtaBookingVersions(bookingId, {
				limit: 1000,
			})
	)
	return { versions: data?.data, isLoading, error }
}
